"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isThetaError } from "@theta/api-client";
import {
  conversationKey,
  useConversation,
  useConversationThread,
  useMarkConversationRead,
  useSendMessage,
} from "@/lib/hooks/useConversation";
import type { Conversation, Message } from "@/lib/api/messages";
import { MessageBubble } from "./MessageBubble";
import { MessageComposer, type ComposedMessage } from "./MessageComposer";
import { ReplyComposerBar } from "./MessageQuote";
import { RequestBanner } from "./RequestBanner";

const TIMESTAMP_STALE_MS = 5 * 60 * 1000;

/**
 * Which messages should render a timestamp, by index.
 *
 * A stamp appears at the start of every run, and again once the one on screen
 * has gone stale. Staleness is measured against the last stamp actually SHOWN,
 * not against the previous message: comparing neighbours lets a steady trickle
 * of sub-threshold gaps drift arbitrarily far from the visible time — four
 * messages four minutes apart span twelve minutes under one 10:00 stamp, and
 * the newest one carries no time at all.
 */
function timestampFlags(messages: Message[]): boolean[] {
  let lastShown = 0;
  return messages.map((message, i) => {
    const at = new Date(message.createdAt).getTime();
    if (startsRun(messages[i - 1], message) || at - lastShown > TIMESTAMP_STALE_MS) {
      lastShown = at;
      return true;
    }
    return false;
  });
}

/** First message of a run: a new author, or anything after a system line. */
function startsRun(prev: Message | undefined, message: Message): boolean {
  return !prev || prev.author?.id !== message.author?.id || prev.kind === "system";
}

/**
 * Whether the thread is scrolled close enough to the bottom to keep following
 * new content. Deliberately loose (100px) so a reader who is only a line or
 * two off the bottom still gets the next message pulled into view.
 *
 * Note it is trivially true of a thread that barely scrolls — including at the
 * very TOP of one, where the bottom is less than 100px away — so it answers
 * "may we pull the view down", never "is the reader done with what's above".
 */
function isAtBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
}

/**
 * One conversation's messages, gate and composer — everything between the
 * header and the bottom edge.
 *
 * Deliberately shell-less: it renders no outer box and no header of its own, so
 * the same carefully-tuned thread logic (scroll anchoring, the bottom pin,
 * mark-read-on-visible) serves all three hosts — the full page, the middle
 * pane of /beskeder, and a docked chat window — instead of being reimplemented
 * per host. The host supplies the frame and, via `renderHeader`, whatever
 * chrome belongs to it.
 */
export function ConversationThread({
  conversationId,
  renderHeader,
  compact = false,
}: {
  conversationId: number;
  /** Rendered above the message list once the conversation has loaded. */
  renderHeader?: (conversation: Conversation) => React.ReactNode;
  /** Tighter padding for the dock. */
  compact?: boolean;
}) {
  // TWO documents, and only one of them is polled (§2.14): the conversation
  // carries the roster, the shared groups and `can`; the messages carry the
  // conditional read. `/details` is folded into the first, so the details
  // pane beside this costs no request of its own.
  //
  // The thread hook owns the message store — the anchored tiling, its
  // `?ifChanged=` tokens and the "Hent ældre beskeder" fetch — so what is left
  // in this component is presentation and the scroll bookkeeping that can only
  // be done where the DOM node is.
  const conversationQuery = useConversation(conversationId);
  const thread = useConversationThread(conversationId);
  const conversation = conversationQuery.data;

  const queryClient = useQueryClient();
  const send = useSendMessage(conversationId);
  const markRead = useMarkConversationRead(conversationId);
  const markReadMutate = markRead.mutate;

  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);
  // Bumped only when an older page is prepended, to drive the scroll anchor.
  const [prependTick, setPrependTick] = useState(0);
  // Reactive, so a mark-read skipped while the tab was hidden is retried on
  // focus. Starts true and is corrected on mount to avoid an SSR mismatch.
  const [isVisible, setIsVisible] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentResizeObserverRef = useRef<ResizeObserver | null>(null);
  const atBottomRef = useRef(true);
  const didInitialScrollRef = useRef(false);
  const prevNewestRef = useRef(0);
  const lastMarkedRef = useRef(0);
  // Reactions on my own messages, as of the last mark-read. See below.
  const lastReactionTotalRef = useRef(0);
  const pendingScrollRef = useRef<{ prevHeight: number; prevTop: number } | null>(
    null
  );

  // The message the composer is currently quoting a reply to, if any. Lives
  // here (not in MessageComposer) because it is set from a MessageBubble
  // click and cleared on send or on route change — both outside the
  // composer's own tree.
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);

  // Reset the imperative scroll/read bookkeeping when the route changes.
  // (The message store resets itself inside `useConversationThread`.)
  useEffect(() => {
    setOlderError(null);
    atBottomRef.current = true;
    didInitialScrollRef.current = false;
    prevNewestRef.current = 0;
    lastMarkedRef.current = 0;
    lastReactionTotalRef.current = 0;
    pendingScrollRef.current = null;
    seenSystemRef.current = null;
    setReplyingTo(null);
  }, [conversationId]);

  const { messages, frontCursor, patchReactions } = thread;
  const newestId =
    messages.length > 0 ? messages[messages.length - 1].id : null;

  /**
   * A SYSTEM LINE IS THE ROSTER CHANGING.
   *
   * "… forlod samtalen" arrives on the message poll, but the roster, the
   * participant count and `can` live on the conversation document, which is
   * deliberately NOT pollable (§2.14). Re-reading it when — and only when — a
   * system line lands keeps the header honest for the price of one request per
   * event, instead of the 1 Hz poll of the whole document this replaced.
   */
  const newestSystemId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].kind === "system") return messages[i].id;
    }
    return null;
  }, [messages]);
  const seenSystemRef = useRef<number | null>(null);

  useEffect(() => {
    if (newestSystemId == null) return;
    const previous = seenSystemRef.current;
    seenSystemRef.current = newestSystemId;
    // The first window a thread loads is history, not an event.
    if (previous !== null && newestSystemId > previous) {
      queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
    }
  }, [newestSystemId, conversationId, queryClient]);

  const fetchOlder = thread.loadOlder;
  const loadOlder = useCallback(async () => {
    if (!frontCursor || loadingOlder) return;
    const el = scrollRef.current;
    if (el) {
      pendingScrollRef.current = {
        prevHeight: el.scrollHeight,
        prevTop: el.scrollTop,
      };
    }
    // Reading history is an explicit move away from the live tail, so stop
    // following it until the reader scrolls back down (onScroll re-arms this
    // the moment they do). Without it the bottom-pinning observer below fights
    // the anchor: `atBottomRef` is set from a 100px-of-the-bottom test, which
    // is ALSO true at the top of a thread that barely scrolls — the docked
    // view, or a thread showing little history — so a stale `true` survives
    // the click, and every resize the prepended page causes (this commit, and
    // each image inside it that finishes loading later) would drag the view
    // back down off the message the anchor just restored.
    atBottomRef.current = false;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      await fetchOlder();
      setPrependTick((t) => t + 1);
    } catch {
      pendingScrollRef.current = null;
      setOlderError("Ældre beskeder kunne ikke hentes. Prøv igen.");
    } finally {
      setLoadingOlder(false);
    }
  }, [fetchOlder, frontCursor, loadingOlder]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = isAtBottom(el);
  }, []);

  // Jump to the bottom on open, and follow new messages only when the reader is
  // already at the bottom — never yank the view during the poll.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;
    if (!didInitialScrollRef.current) {
      el.scrollTop = el.scrollHeight;
      didInitialScrollRef.current = true;
      prevNewestRef.current = newestId ?? 0;
      return;
    }
    if (newestId != null && newestId > prevNewestRef.current) {
      prevNewestRef.current = newestId;
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    }
  }, [newestId, messages.length]);

  // Keep the view pinned to the bottom as the thread's own content grows —
  // images, link-preview cards, YouTube embeds and audio players all finish
  // loading/measuring AFTER the initial paint, and every one of them changes
  // scrollHeight once it does. The effect that jumps to the bottom above only
  // reacts to a NEW MESSAGE arriving; without this, a message that lands with
  // an image is left at the wrong scroll position once the image grows the
  // thread out from under an already-fired scrollTop = scrollHeight (OSO-384).
  //
  // A callback ref rather than a plain useRef + useEffect([]): the loading
  // skeleton renders in place of this content on the very first commit (see
  // the `isLoading` branch below), so a mount-once effect would see a null
  // node and never attach anything — this instead observes exactly when the
  // real content's DOM node appears, whichever render that happens to be.
  // atBottomRef and scrollRef are read fresh inside the callback, so one
  // observer serves every conversation the component is reused for.
  const setContentRef = useCallback((node: HTMLDivElement | null) => {
    contentResizeObserverRef.current?.disconnect();
    contentResizeObserverRef.current = null;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      const el = scrollRef.current;
      // A pending `pendingScrollRef` means the anchor below owns the scroll
      // position (an older page is on its way), so leave it alone. Belt and
      // braces next to the `atBottomRef` reset in loadOlder: observer
      // callbacks land after the layout effects of the same commit, which is
      // precisely when the anchor has just written a scrollTop, and nothing
      // here should depend on that ordering holding.
      if (!el || pendingScrollRef.current) return;
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(node);
    contentResizeObserverRef.current = observer;
  }, []);

  // Keep the reading position stable when an older page is prepended.
  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    pendingScrollRef.current = null;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight - pending.prevHeight + pending.prevTop;
  }, [prependTick]);

  // Track visibility reactively. A conversation opened in a background tab
  // (cmd-click from the inbox) resolves its first fetch while hidden; without
  // this the skipped mark-read was never retried, because the newest id does
  // not change on focus — the thread stayed unread and the delayed email was
  // never cancelled.
  useEffect(() => {
    const sync = () => setIsVisible(document.visibilityState !== "hidden");
    sync(); // catches a tab that was already hidden at mount
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  /**
   * A REACTION ON A MESSAGE OF MINE, counted — OSO-550.
   *
   * The 1 Hz `/messages` poll brings somebody else's reaction in and the pill
   * renders; nothing else about the thread moves. This total is what the
   * mark-read effect below watches, because the signal that reaction raised is
   * on the seat and only a read can lower it. Reactions on OTHER people's
   * messages are excluded: they raise no signal for this viewer, so counting
   * them would be a `POST /read` per pill anybody in the room stands.
   */
  const myReactionTotal = useMemo(
    () =>
      messages.reduce(
        (total, m) =>
          m.isMine
            ? total + m.reactions.reduce((n, r) => n + r.count, 0)
            : total,
        0,
      ),
    [messages],
  );

  /**
   * Mark read on open, whenever a newer message arrives while visible — and
   * whenever somebody reacts to one of my messages in the thread I am looking
   * at.
   *
   * The last clause is not a nicety. A reaction writes no message, so
   * `newestId` does not move and the guard returns on exactly the read that is
   * meant to clear the signal: with the thread open, the pill appeared while
   * the inbox ring and the header's "Nyt i dine beskeder" stayed lit until a
   * new message arrived or the reader navigated away and back. "Prikken
   * forsvinder, når du har åbnet samtalen" failed for the one case the feature
   * exists for. (A RENAME needs nothing here — it writes a system line, so
   * `newestId` moves on its own.)
   *
   * `POST /read` lowers the flag above its own marker-did-not-move return, so
   * one call still clears everything. Only an INCREASE counts: a withdrawn
   * reaction raises no signal, and re-reading on the way down would be a
   * request for nothing. Prepending an older page also increases it and sends
   * one extra read — the write is guarded on the flag actually being up, so it
   * costs a round trip and touches no row.
   */
  useEffect(() => {
    if (!newestId || !isVisible) return;
    const reacted = myReactionTotal > lastReactionTotalRef.current;
    if (newestId <= lastMarkedRef.current && !reacted) {
      lastReactionTotalRef.current = myReactionTotal;
      return;
    }
    lastMarkedRef.current = newestId;
    lastReactionTotalRef.current = myReactionTotal;
    markReadMutate(newestId);
  }, [newestId, isVisible, myReactionTotal, markReadMutate]);

  /**
   * Bots that only answer when greeted by name — a GROUP conversation's seated
   * bots. In a 1:1 with a bot every message is answered, so nothing is passed
   * and the composer has nothing to point out.
   */
  const participants = conversation?.participants;
  const groupDmBots = useMemo(
    () =>
      participants && participants.length > 2
        ? participants
            .filter((p) => !p.isSelf && p.user.isBot)
            .map((p) => ({ id: p.user.id, name: p.user.name ?? "" }))
        : undefined,
    [participants],
  );

  const handleSend = useCallback(
    async (composed: ComposedMessage) => {
      atBottomRef.current = true;
      await send.mutateAsync({
        bodyMarkdown: composed.text,
        ...(replyingTo ? { replyToMessageId: replyingTo.id } : {}),
        ...(composed.attachments.length > 0
          ? { attachments: composed.attachments }
          : {}),
      });
      setReplyingTo(null);
    },
    [send, replyingTo]
  );

  const error = conversationQuery.error ?? thread.error;
  if (error) {
    // A 404 IS THE ANSWER, not a failure: a non-participant, a left seat and a
    // conversation that never existed are told the same thing on purpose (D7).
    const isNotFound = isThetaError(error, "notFound");
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-center">
        <p className={`text-sm ${isNotFound ? "text-subtle-foreground" : "text-destructive"}`}>
          {isNotFound
            ? "Samtalen findes ikke"
            : "Samtalen kunne ikke hentes. Prøv igen."}
        </p>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex-1 p-4 space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-border p-4 animate-pulse"
          >
            <div className="h-3 bg-muted rounded w-3/4" />
            <div className="h-2 bg-muted rounded w-1/3 mt-2" />
          </div>
        ))}
      </div>
    );
  }

  // Not yet accepted: the server still sends the messages — withholding them
  // is a presentation rule in this view, not an access rule — so the gate
  // stands in front of them until the viewer decides.
  if (conversation.isRequest) {
    return (
      <>
        {renderHeader?.(conversation)}
        {/* `flex items-center` centres the gate vertically in the empty
            thread. It also makes the banner a flex item on the HORIZONTAL
            axis, where `min-width: auto` floors it at min-content — hence the
            `min-w-0` the banner carries. See the comment on its root. */}
        <div className="flex-1 min-h-0 overflow-y-auto flex items-center">
          <RequestBanner conversation={conversation} messages={messages} />
        </div>
      </>
    );
  }

  const isGroup = conversation.isGroup;
  const timestampShown = timestampFlags(messages);

  return (
    <>
      {renderHeader?.(conversation)}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        /* This scroller clips whatever a message bubble opens — the reaction
           picker's popover most visibly, and in the chat dock it is a great
           deal shorter than the window (OSO-560). */
        data-popover-bounds=""
        className={`flex-1 overflow-y-auto no-scrollbar ${compact ? "px-3 py-2" : "px-4 py-3"}`}
      >
        <div ref={setContentRef} className="space-y-2">
          {frontCursor && (
            <div className="text-center pb-2">
              <button
                type="button"
                onClick={loadOlder}
                disabled={loadingOlder}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {loadingOlder ? "Henter…" : "Hent ældre beskeder"}
              </button>
            </div>
          )}

          {olderError && (
            <p className="text-center text-xs text-destructive">
              {olderError}
            </p>
          )}

          {messages.length === 0 && (
            <p className="text-center text-subtle-foreground text-sm py-12">
              Ingen beskeder endnu
            </p>
          )}

          {messages.map((message, i) => (
            <MessageBubble
              key={message.id}
              message={message}
              showAuthor={isGroup ? true : startsRun(messages[i - 1], message)}
              showTimestamp={timestampShown[i]}
              conversationId={conversationId}
              canReport={conversation.can.report}
              onReply={setReplyingTo}
              onReacted={patchReactions}
            />
          ))}
        </div>
      </div>

      {/* `can.reply` decides whether there is a composer at all — never
          "does the conversation exist". A seat that has been declined, or a
          conversation somebody left, reads fine and cannot be written to. */}
      {conversation.can.reply && (
        <div className={`border-t border-border bg-surface ${compact ? "px-2 py-2" : "px-4 py-3"}`}>
          {replyingTo && (
            <ReplyComposerBar
              message={replyingTo}
              onCancel={() => setReplyingTo(null)}
            />
          )}
          <MessageComposer
            draftScope={`message:${conversationId}`}
            uploadScope={{ type: "conversation", id: conversationId }}
            onSend={handleSend}
            bots={groupDmBots}
          />
        </div>
      )}
    </>
  );
}
